/**
 * Billing settings domain API functions (Phase 1b Layer A backend).
 *
 * GET + PATCH /api/admin/billing-settings — singleton row per organization.
 * Backend is at routes_admin_billing_settings.py; the row is auto-seeded by
 * migration 097 and lazily created by the service if missing.
 */
import { API_BASE_URL, getApiHeaders } from "./core";


/** GET /api/admin/billing-settings — full 32-field object for caller's org. */
export function fetchAdminBillingSettings() {
  return fetch(`${API_BASE_URL}/api/admin/billing-settings`, {
    headers: getApiHeaders(),
  }).then((res) => {
    if (!res.ok) throw new Error("Einstellungen konnten nicht geladen werden.");
    return res.json();
  });
}


/**
 * Extract human-friendly field names from FastAPI's 422 ``detail`` array.
 * Each item has shape ``{loc: ["body", "<field>"], msg: "...", ...}`` —
 * we strip the leading "body" and take the last remaining segment as the
 * field name. Returns a sorted, deduped list of field names, or [] if the
 * shape doesn't match.
 */
function _extractFieldNames(detail) {
  if (!Array.isArray(detail)) return [];
  const names = new Set();
  for (const item of detail) {
    if (!item || typeof item !== "object") continue;
    const loc = Array.isArray(item.loc) ? item.loc : null;
    if (!loc || loc.length === 0) continue;
    // Drop leading "body" / "query" / "path" framing, take the last segment.
    const trimmed = loc.filter((seg) => seg !== "body");
    const name = trimmed.length > 0 ? trimmed[trimmed.length - 1] : null;
    if (name != null && name !== "") {
      names.add(String(name));
    }
  }
  return Array.from(names).sort();
}


/**
 * PATCH /api/admin/billing-settings — partial update, returns full object.
 *
 * Body: only the fields the caller wants to change. The backend ignores
 * id/organization_id and applies explicit None to clear optional fields.
 *
 * On 422, the error message includes the field names that failed validation,
 * extracted from FastAPI's standard detail array. On other failures, falls
 * back to a generic German error string.
 */
export function updateAdminBillingSettings(body) {
  return fetch(`${API_BASE_URL}/api/admin/billing-settings`, {
    method: "PATCH",
    headers: getApiHeaders(),
    body: JSON.stringify(body),
  }).then(async (res) => {
    if (res.ok) return res.json();
    let parsed = null;
    try {
      parsed = await res.json();
    } catch (_) {
      /* not JSON */
    }
    const fields = parsed ? _extractFieldNames(parsed.detail) : [];
    if (fields.length > 0) {
      throw new Error(`Validierungsfehler in: ${fields.join(", ")}`);
    }
    throw new Error("Einstellungen konnten nicht gespeichert werden.");
  });
}
